import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, X, AlertCircle, CheckCircle, Eye, Plus, Wrench } from 'lucide-react';
import axios from 'axios';
import { Button, FormField, Input, Select, Textarea, FormSection, FormGrid, AlertMessage, FileUploadManager, DataTable, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell, StatusBadge, IconButton, EmptyState } from '../common/ui';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { DEVICE_STATUS_OPTIONS } from '../../utils/statusUtils';
import RepairActionButtons from '../repairs/components/RepairActionButtons';

const DeviceEdit = () => {
  const { serialNumber } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  
  // Determine if we're in create or edit mode
  const isCreateMode = !serialNumber;
  
  // Get pre-selected product from URL params
  const preselectedProduct = searchParams.get('product');
  
  // Device basic info
  const [deviceData, setDeviceData] = useState({
    id: '',
    serial_number: '',
    model: '',
    status: 'active',
    purchase_date: '',
    warranty_expiry: '',
    notes: '',
    order_number: '',
    order_id: null
  });

  // Product configuration (predefined attributes and modules)
  const [productConfig, setProductConfig] = useState({
    product: null,
    productAttributes: [],
    assignedModules: [],
    moduleAttributesMap: {}
  });
  
  // Device attribute values (user's input for predefined attributes)
  const [deviceAttributeValues, setDeviceAttributeValues] = useState({});
  
  // Device module attribute values (user's input for predefined module attributes)
  const [deviceModuleAttributeValues, setDeviceModuleAttributeValues] = useState({});
  
  
  // Reference data
  const [users, setUsers] = useState([]);

  // Repairs history data
  const [repairs, setRepairs] = useState([]);
  const [repairsLoading, setRepairsLoading] = useState(false);
  const [repairsError, setRepairsError] = useState('');

  // Module repairs state
  const [moduleRepairs, setModuleRepairs] = useState({}); // { moduleName: [repairs] }
  const [activeRepairModule, setActiveRepairModule] = useState(null); // Which module's repair form is open
  const [repairFormData, setRepairFormData] = useState({
    repair_description: '',
    repair_type: ''
  });
  const [repairSaving, setRepairSaving] = useState(false);
  const [repairError, setRepairError] = useState('');

  // Map legacy attribute names to current product attribute names
  const mapLegacyAttributeName = (legacyName) => {
    const nameMapping = {
      'FlarmID': 'Flarm ID',
      'navigator_version': 'Navigator version',
      'OS_build_number': 'OS build number',
      'oudie_home_version': 'Home version',
      'Antenna_type_Oudie_N': 'Antenna type Oudie N',
      'fanet__type': 'Fanet module type',
      'Oudie_housing_type': 'Oudie housing type'
    };
    
    return nameMapping[legacyName] || legacyName;
  };

  // Map legacy module attribute names to current module attribute names
  const mapLegacyModuleAttributeName = (legacyName) => {
    const nameMapping = {
      'LCDseries': 'LCD Series',
      'BatterySeries': 'Battery Series'
    };
    
    return nameMapping[legacyName] || legacyName;
  };

  // Fetch all reference data and device data
  useEffect(() => {
    // If in create mode but no product is selected, redirect to product selection
    if (isCreateMode && !preselectedProduct) {
      navigate('/devices/select-product');
      return;
    }
    fetchInitialData();
  }, [serialNumber, isCreateMode, preselectedProduct, navigate]);

  // Fetch repairs history when device data is loaded (only in edit mode)
  useEffect(() => {
    if (!isCreateMode && deviceData.id) {
      fetchRepairsHistory();
    }
  }, [isCreateMode, deviceData.id]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (isCreateMode) {
        // For create mode, only fetch reference data
        const usersRes = await axios.get('/api/users/basic');
        setUsers(usersRes.data);
        
        // Use the pre-selected product from URL params
        
        // Initialize device data with pre-selected product if available
        setDeviceData({
          id: '',
          serial_number: '',
          model: preselectedProduct || '',
          status: 'active',
          purchase_date: '',
          warranty_expiry: '',
          notes: '',
          order_number: ''
        });
        
        setDeviceAttributeValues({});
        setDeviceModuleAttributeValues({});
        
        // If we have a pre-selected product, fetch its configuration
        if (preselectedProduct) {
          await fetchProductConfiguration(preselectedProduct, false);
        }
      } else {
        // For edit mode, fetch reference data and device data
        const [usersRes, deviceRes] = await Promise.all([
          axios.get('/api/users/basic'),
          axios.get(`/api/devices/serial/${serialNumber}`)
        ]);

        setUsers(usersRes.data);
        
        // Set device data
        const device = deviceRes.data;
        setDeviceData({
          id: device.id || '',
          serial_number: device.serial_number || '',
          product_name: device.product_name || device.model || '',
          product_id: device.product_id || null,
          status: device.status || 'active',
          purchase_date: device.purchase_date ? device.purchase_date.split('T')[0] : '',
          warranty_expiry: device.warranty_expiry ? device.warranty_expiry.split('T')[0] : '',
          notes: device.notes || '',
          order_id: device.order_id || null
        });

        // Load existing device attribute values with name mapping for legacy compatibility
        const attributeValues = {};
        (device.attributes || []).forEach(attr => {
          // Map legacy attribute names to current product attribute names
          const mappedName = mapLegacyAttributeName(attr.name);
          attributeValues[mappedName] = attr.value;
          
          // Also store the original name in case there's no mapping
          if (mappedName !== attr.name) {
            attributeValues[attr.name] = attr.value;
          }
        });
        setDeviceAttributeValues(attributeValues);

        // Load existing device module attribute values with name mapping for legacy compatibility
        const moduleAttrValues = {};
        (device.moduleAttributes || []).forEach(attr => {
          const mappedName = mapLegacyModuleAttributeName(attr.attribute_name);
          const key = `${attr.module_id}_${mappedName}`;
          moduleAttrValues[key] = attr.attribute_value;
          
          // Also store the original name in case there's no mapping
          if (mappedName !== attr.attribute_name) {
            const originalKey = `${attr.module_id}_${attr.attribute_name}`;
            moduleAttrValues[originalKey] = attr.attribute_value;
          }
        });
        setDeviceModuleAttributeValues(moduleAttrValues);

        // Fetch product configuration if product_name or model is set
        const productName = device.product_name || device.model;
        if (productName) {
          await fetchProductConfiguration(productName, true); // Include hidden attributes for existing devices
        }
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.error || (isCreateMode ? 'Failed to load reference data' : 'Failed to load device data'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceDataChange = (field, value) => {
    setDeviceData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleOrderNumberBlur = (e) => {
    const { value } = e.target;
    const trimmedValue = value.trim();
    
    // Convert number to URL only when field loses focus
    if (trimmedValue && /^\d+$/.test(trimmedValue)) {
      const generatedUrl = `https://b2b.naviter.com/wp-admin/admin.php?page=wc-orders&action=edit&id=${trimmedValue}`;
      setDeviceData(prev => ({
        ...prev,
        order_number: generatedUrl
      }));
    }
    // If it's already a URL or empty, leave it unchanged
  };

  const fetchProductConfiguration = async (productName, includeHidden = false) => {
    try {
      const url = includeHidden 
        ? `/api/devices/product-config/${encodeURIComponent(productName)}?includeHidden=true`
        : `/api/devices/product-config/${encodeURIComponent(productName)}`;
      const response = await axios.get(url);
      setProductConfig(response.data);
      
      // Set product_id in deviceData if we're in create mode and have a product
      if (isCreateMode && response.data.product && response.data.product.id) {
        setDeviceData(prev => ({
          ...prev,
          product_id: response.data.product.id
        }));
      }
      
      // Initialize default values for product attributes in create mode
      if (isCreateMode && response.data.productAttributes) {
        const defaultProductAttrs = {};
        response.data.productAttributes.forEach(attr => {
          if (!attr.hidden && attr.default_value) {
            defaultProductAttrs[attr.name] = attr.default_value;
          }
        });
        if (Object.keys(defaultProductAttrs).length > 0) {
          setDeviceAttributeValues(prev => ({
            ...prev,
            ...defaultProductAttrs
          }));
        }
      }
      
      // Initialize default values for module attributes in create mode
      if (isCreateMode && response.data.assignedModules && response.data.moduleAttributesMap) {
        const defaultModuleAttrs = {};
        response.data.assignedModules.forEach(module => {
          if (!module.hidden) {
            const moduleAttrs = response.data.moduleAttributesMap[module.id] || [];
            moduleAttrs.forEach(attr => {
              if (!attr.hidden && attr.default_value) {
                const key = `${module.id}_${attr.name}`;
                defaultModuleAttrs[key] = attr.default_value;
              }
            });
          }
        });
        if (Object.keys(defaultModuleAttrs).length > 0) {
          setDeviceModuleAttributeValues(prev => ({
            ...prev,
            ...defaultModuleAttrs
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching product configuration:', error);
      // Don't show error to user for this, just log it
    }
  };

  const fetchRepairsHistory = async () => {
    try {
      setRepairsLoading(true);
      setRepairsError('');
      
      const response = await axios.get(`/api/repairs?device_id=${deviceData.id}&limit=100`);
      
      // Handle repairs data
      const repairsData = response.data.repairs || response.data;
      if (Array.isArray(repairsData)) {
        setRepairs(repairsData);
      } else {
        setRepairs([]);
        setRepairsError('Invalid repairs data format received from server');
      }
    } catch (error) {
      console.error('Error fetching repairs history:', error);
      setRepairsError('Failed to fetch repairs history');
      setRepairs([]);
    } finally {
      setRepairsLoading(false);
    }
  };


  const updateDeviceAttributeValue = (attributeName, value) => {
    setDeviceAttributeValues(prev => ({
      ...prev,
      [attributeName]: value
    }));
    
    // Clear error for this attribute if it exists
    if (errors[attributeName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[attributeName];
        return newErrors;
      });
    }
  };

  const updateDeviceModuleAttributeValue = (moduleId, attributeName, value) => {
    const key = `${moduleId}_${attributeName}`;
    setDeviceModuleAttributeValues(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Clear error for this module attribute if it exists
    if (errors[key]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  // Repair action handlers
  const handleViewRepair = (repair) => {
    navigate(`/repairs/${repair.id}/view`);
  };

  const handleCreateRepair = () => {
    navigate('/repairs/new', { 
      state: { 
        preselectedDevice: {
          id: deviceData.id,
          serial_number: deviceData.serial_number,
          product_name: deviceData.product_name || deviceData.model,
          product_id: deviceData.product_id
        }
      }
    });
  };

  // Module repair handlers
  const fetchModuleRepairs = async (moduleName) => {
    try {
      console.log('Fetching repairs for module:', moduleName, 'device ID:', deviceData.id);
      const response = await axios.get(`/api/devices/${deviceData.id}/modules/${moduleName}/repairs`);
      console.log('Module repairs fetched:', response.data);
      setModuleRepairs(prev => ({
        ...prev,
        [moduleName]: response.data
      }));
    } catch (err) {
      console.error('Error fetching module repairs:', err);
    }
  };

  const handleOpenRepairForm = (moduleName) => {
    console.log('Opening repair form for module:', moduleName);
    console.log('Device ID:', deviceData.id);
    setActiveRepairModule(moduleName);
    setRepairFormData({
      repair_description: '',
      repair_type: ''
    });
    setRepairError('');
    // Fetch existing repairs for this module
    fetchModuleRepairs(moduleName);
  };

  const handleCloseRepairForm = () => {
    setActiveRepairModule(null);
    setRepairFormData({
      repair_description: '',
      repair_type: ''
    });
    setRepairError('');
  };

  const handleRepairFormChange = (field, value) => {
    setRepairFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveRepair = async (moduleName) => {
    // Validation
    if (!repairFormData.repair_type) {
      setRepairError('Please select a repair type');
      return;
    }

    try {
      setRepairSaving(true);
      setRepairError('');

      await axios.post(`/api/devices/${deviceData.id}/modules/${moduleName}/repair`, {
        repair_description: repairFormData.repair_description,
        repair_type: repairFormData.repair_type,
        performed_by: user.id
      });

      // Refresh module repairs
      await fetchModuleRepairs(moduleName);
      
      // Clear form but keep repairs visible
      setRepairFormData({
        repair_description: '',
        repair_type: ''
      });
      
      setSuccess(`${moduleName} repair recorded successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving module repair:', err);
      setRepairError(err.response?.data?.error || 'Failed to save repair');
    } finally {
      setRepairSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Validate only serial_number and product_id for draft
      if (!deviceData.serial_number || !deviceData.product_id) {
        setError('Serial number and product are required');
        return;
      }

      // Prepare data for API with draft status
      const saveData = {
        ...deviceData,
        status: 'draft',
        // Convert empty strings to null for numeric/date fields to avoid database errors
        purchase_date: deviceData.purchase_date || null,
        warranty_expiry: deviceData.warranty_expiry || null
      };
      // Remove deprecated/read-only fields
      delete saveData.model;
      delete saveData.order_number;
      delete saveData.owner_name;
      delete saveData.owner_email;

      let deviceId;
      
      if (isCreateMode) {
        // Create new device as draft
        const response = await axios.post('/api/devices', saveData);
        deviceId = response.data.id;
        
        // Update deviceData with the new ID
        setDeviceData(prev => ({
          ...prev,
          id: deviceId,
          status: 'draft'
        }));
        
        setSuccess('Device saved as draft successfully!');
        
        // Navigate to view page after saving as draft
        setTimeout(() => {
          navigate(`/devices/id/${deviceId}/view`);
        }, 1500);
      } else {
        // Update existing device
        await axios.put(`/api/devices/${deviceData.id}`, saveData);
        deviceId = deviceData.id;
        setSuccess('Device saved as draft successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }

      // Save device attributes (if product config is loaded and we have attributes)
      // Note: We save attributes even for draft, but don't validate required ones
      if (productConfig.productAttributes.length > 0) {
        for (const attr of productConfig.productAttributes) {
          const value = deviceAttributeValues[attr.name] || '';
          if (attr.name) {
            await axios.post(`/api/devices/${deviceId}/attributes`, {
              name: attr.name,
              value: value,
              type: attr.type
            });
          }
        }

        // Save device module attributes
        for (const module of productConfig.assignedModules) {
          const moduleAttrs = productConfig.moduleAttributesMap[module.id] || [];
          for (const attr of moduleAttrs) {
            const key = `${module.id}_${attr.name}`;
            const value = deviceModuleAttributeValues[key] || '';
            if (attr.name) {
              await axios.post(`/api/devices/${deviceId}/module-attributes`, {
                module_id: module.id,
                attribute_name: attr.name,
                attribute_value: value
              });
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Error saving device as draft:', error);
      setError(error.response?.data?.error || 'Failed to save device as draft');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setErrors({});
      setSuccess('');

      // Validate required fields
      console.log('Validating device data:', {
        serial_number: deviceData.serial_number,
        product_id: deviceData.product_id,
        product_name: deviceData.product_name,
        model: deviceData.model,
        isCreateMode
      });

      const newErrors = {};
      if (!deviceData.serial_number) newErrors.serial_number = 'Serial number is required';
      if (!deviceData.product_id) newErrors.product_id = 'Product is required';

      // Validate required attributes if in create mode
      if (isCreateMode) {
        // Check required product attributes
        for (const attr of productConfig.productAttributes) {
          if (attr.required && !attr.hidden) {
            const value = deviceAttributeValues[attr.name];
            if (!value || (typeof value === 'string' && value.trim() === '')) {
              newErrors[attr.name] = `${attr.name} is required`;
            }
          }
        }

        // Check required module attributes
        for (const module of productConfig.assignedModules) {
          if (!module.hidden) {
            const moduleAttrs = productConfig.moduleAttributesMap[module.id] || [];
            for (const attr of moduleAttrs) {
              if (attr.required && !attr.hidden) {
                const key = `${module.id}_${attr.name}`;
                const value = deviceModuleAttributeValues[key];
                if (!value || (typeof value === 'string' && value.trim() === '')) {
                  newErrors[key] = `${attr.name} is required`;
                }
              }
            }
          }
        }

        // Ensure status is not draft when creating device
        if (deviceData.status === 'draft') {
           setError('Cannot create device with draft status. Use Save button to save as draft.');
           setSaving(false);
           return;
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setError('Please fix the errors below.');
        setSaving(false);
        
        // Scroll to the first missing field
        setTimeout(() => {
          const firstErrorKey = Object.keys(newErrors)[0];
          // Try to find element by name (for standard inputs) or data attribute (for dynamic attributes)
          let element = document.querySelector(`[name="${firstErrorKey}"]`);
          
          if (!element) {
             // Try finding by data attribute for product attributes
             const attrSection = document.querySelector(`[data-attr-name="${firstErrorKey}"]`);
             if (attrSection) {
               element = attrSection.querySelector('input, select');
             }
          }
          
          if (!element) {
             // Try finding by data-error-key for module attributes
             const moduleAttrSection = document.querySelector(`[data-error-key="${firstErrorKey}"]`);
             if (moduleAttrSection) {
                element = moduleAttrSection.querySelector('input, select');
             }
          }

          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          } else {
             // Fallback: scroll to top
             window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 100);
        
        return;
      }

      // Prepare data for API
      const saveData = {
        ...deviceData,
        // Convert empty strings to null for numeric/date fields to avoid database errors
        purchase_date: deviceData.purchase_date || null,
        warranty_expiry: deviceData.warranty_expiry || null,
        // Add performed_by for user tracking
        performed_by: user?.id || null
      };
      // Remove deprecated/read-only fields
      delete saveData.model;
      delete saveData.order_number;
      delete saveData.owner_name;
      delete saveData.owner_email;

      let deviceId;
      
      if (isCreateMode) {
        // Create new device
        const response = await axios.post('/api/devices', saveData);
        deviceId = response.data.id;
        setSuccess('Device created successfully!');
        
        // Navigate to view page after creation
        setTimeout(() => {
          navigate(`/devices/id/${deviceId}/view`);
        }, 1500);
      } else {
        // Update existing device
        await axios.put(`/api/devices/${deviceData.id}`, saveData);
        deviceId = deviceData.id;
      setSuccess('Device updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }

      // Save device attributes (if product config is loaded and we have attributes)
      if (productConfig.productAttributes.length > 0) {
        for (const attr of productConfig.productAttributes) {
          const value = deviceAttributeValues[attr.name] || '';
          if (attr.name) {
            await axios.post(`/api/devices/${deviceId}/attributes`, {
              name: attr.name,
              value: value,
              type: attr.type
            });
          }
        }

        // Save device module attributes
        for (const module of productConfig.assignedModules) {
          const moduleAttrs = productConfig.moduleAttributesMap[module.id] || [];
          for (const attr of moduleAttrs) {
            const key = `${module.id}_${attr.name}`;
            const value = deviceModuleAttributeValues[key] || '';
            if (attr.name) {
              await axios.post(`/api/devices/${deviceId}/module-attributes`, {
                module_id: module.id,
                attribute_name: attr.name,
                attribute_value: value
              });
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Error saving device:', error);
      setError(error.response?.data?.error || `Failed to ${isCreateMode ? 'create' : 'save'} device`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const getActionButtons = () => {
    const buttons = {};
    
    // Back button - always shown
    buttons.backButton = (
      <Button
        onClick={() => navigate(-1)}
        variant="ghost"
        size="xs"
        icon={ArrowLeft}
        className="flex-shrink-0 px-0"
      >
        Back
      </Button>
    );
    
    // Action buttons - Cancel and Save (draft) are non-CTA buttons
    buttons.actionButtons = [
      <Button
        key="cancel"
        onClick={handleCancel}
        variant="secondary"
        size="sm"
        icon={X}
        disabled={saving}
      >
        Cancel
      </Button>
    ];

    // Save draft button (only in create mode)
    if (isCreateMode) {
      buttons.actionButtons.push(
        <Button
          key="save-draft"
          onClick={handleSaveDraft}
          variant="secondary"
          size="sm"
          icon={Save}
          loading={saving}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      );
    }
    
    // CTA button - Create Device / Save Changes
    buttons.ctaButton = (
      <Button
        onClick={handleSave}
        variant="primary"
        size="md"
        icon={Save}
        loading={saving}
        disabled={saving}
      >
        {saving 
          ? (isCreateMode ? 'Creating...' : 'Saving...') 
          : (isCreateMode ? 'Create Device' : 'Save Changes')
        }
      </Button>
    );
    
    return buttons;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-naviter-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">{isCreateMode ? 'Loading...' : 'Loading device...'}</p>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
        <div className="mb-4">
          <div className="space-y-4">
            {/* Title */}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isCreateMode ? 'Add New Device' : 'Edit Device'}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {isCreateMode 
                  ? 'Enter device information to add it to the system'
                  : `${deviceData.serial_number} - ${deviceData.model}`
                }
              </p>
            </div>

            {/* Action Buttons */}
            <RepairActionButtons {...getActionButtons()} />
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && <AlertMessage type="error" message={error} />}
        {success && <AlertMessage type="success" message={success} />}

        <div className="space-y-4">
          {/* Basic Information */}
          <FormSection title="Basic Information">
              <FormGrid columns={2}>
                <FormField label="Product" required error={errors.product_id} size="sm">
                  <Input
                    value={deviceData.product_name || deviceData.model || ''}
                    readOnly
                    size="sm"
                    title="Product type cannot be changed after device creation"
                    error={!!errors.product_id}
                  />
                </FormField>
                
                <FormField label="Serial Number" required error={errors.serial_number} size="sm">
                  <Input
                    name="serial_number"
                    value={deviceData.serial_number}
                    onChange={(e) => handleDeviceDataChange('serial_number', e.target.value)}
                    size="sm"
                    required
                    error={!!errors.serial_number}
                  />
                </FormField>
                
                <FormField label="Status" size="sm">
                  <Select
                    value={deviceData.status}
                    onChange={(e) => handleDeviceDataChange('status', e.target.value)}
                    options={DEVICE_STATUS_OPTIONS}
                    size="sm"
                  />
                </FormField>

                {!isCreateMode && (
                  <>
                    <FormField label="Purchase Date" size="sm">
                      <Input
                        type="date"
                        size="sm"
                        value={deviceData.purchase_date}
                        onChange={(e) => handleDeviceDataChange('purchase_date', e.target.value)}
                      />
                    </FormField>
                    
                    <FormField label="Warranty Expiry" size="sm">
                      <Input
                        type="date"
                        size="sm"
                        value={deviceData.warranty_expiry}
                        onChange={(e) => handleDeviceDataChange('warranty_expiry', e.target.value)}
                      />
                    </FormField>

                    <FormField label="Order Link" size="sm">
                      {deviceData.order_id ? (
                        <div>
                          <Input
                            type="url"
                            value={deviceData.order_number || ''}
                            disabled
                            size="sm"
                            className="bg-gray-50 cursor-not-allowed"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Order information is managed on the{' '}
                            <a
                              href={`#/orders/${deviceData.order_id}/serials`}
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/orders/${deviceData.order_id}/serials`);
                              }}
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              order serials page
                            </a>
                          </p>
                        </div>
                      ) : (
                        <Input
                          type="url"
                          value={deviceData.order_number}
                          onChange={(e) => handleDeviceDataChange('order_number', e.target.value)}
                          onBlur={handleOrderNumberBlur}
                          size="sm"
                          placeholder="https://naviter.com/wp-admin/admin.php?page=wc-orders&action=edit&id=12345"
                        />
                      )}
                    </FormField>
                  </>
                )}
              </FormGrid>
              
              <div className="mt-3">
                <FormField label="Notes" size="sm">
                  <Textarea
                    rows={3}
                    size="sm"
                    value={deviceData.notes}
                    onChange={(e) => handleDeviceDataChange('notes', e.target.value)}
                    placeholder="Additional notes about this device..."
                  />
                </FormField>
              </div>
            </FormSection>

          {/* Product Attributes - Show in edit mode or create mode with selected product */}
          {((isCreateMode && deviceData.model) || !isCreateMode) && productConfig.productAttributes.length > 0 && (
            <FormSection title="Product Attributes">
              <div className="space-y-3">
                {productConfig.productAttributes.filter(attr => !attr.hidden).map((attr, index) => (
                    <div key={attr.id} data-attr-name={attr.name} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                      <label className="block text-xs font-medium text-gray-700 mb-0.5">
                        {attr.name}
                        {attr.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {attr.type === 'boolean' ? (
                        <div className="flex gap-2">
                          {[
                            { value: 'true', label: 'Yes' },
                            { value: 'false', label: 'No' }
                          ].map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => updateDeviceAttributeValue(attr.name, opt.value)}
                              className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                (deviceAttributeValues[attr.name] || 'false') === opt.value
                                  ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      ) : attr.type === 'dropdown' ? (
                        attr.options && Array.isArray(attr.options) && attr.options.length <= 5 ? (
                          <div className="flex gap-2">
                            {attr.options.map((option, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => updateDeviceAttributeValue(attr.name, option)}
                                className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                  (deviceAttributeValues[attr.name] || '') === option
                                    ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <select
                            value={deviceAttributeValues[attr.name] || ''}
                            onChange={(e) => updateDeviceAttributeValue(attr.name, e.target.value)}
                            className={`w-full h-7 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent text-xs ${errors[attr.name] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                          >
                            <option value="">Select...</option>
                            {attr.options && Array.isArray(attr.options) && attr.options.map((option, idx) => (
                              <option key={idx} value={option}>{option}</option>
                            ))}
                          </select>
                        )
                      ) : attr.type === 'date' ? (
                        <input
                          type="date"
                          value={deviceAttributeValues[attr.name] || ''}
                          onChange={(e) => updateDeviceAttributeValue(attr.name, e.target.value)}
                          className={`w-full h-7 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent text-xs ${errors[attr.name] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                        />
                      ) : attr.type === 'number' ? (
                        <input
                          type="number"
                          value={deviceAttributeValues[attr.name] || ''}
                          onChange={(e) => updateDeviceAttributeValue(attr.name, e.target.value)}
                          className={`w-full h-7 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent text-xs ${errors[attr.name] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                        />
                      ) : (
                        <input
                          type="text"
                          value={deviceAttributeValues[attr.name] || ''}
                          onChange={(e) => updateDeviceAttributeValue(attr.name, e.target.value)}
                          className={`w-full h-7 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent text-xs ${errors[attr.name] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                          placeholder={`Enter ${attr.name.toLowerCase()}`}
                        />
                      )}
                      {errors[attr.name] && (
                        <p className="mt-1 text-xs text-red-600">{errors[attr.name]}</p>
                      )}
                    </div>
                  ))}
              </div>
            </FormSection>
          )}

          {/* Module Attributes Grouped by Module - Show in edit mode or create mode with selected product */}
          {((isCreateMode && (deviceData.product_name || deviceData.model)) || !isCreateMode) && productConfig.assignedModules.filter(module => !module.hidden).map((module) => {
              const moduleAttrs = (productConfig.moduleAttributesMap[module.id] || []).filter(attr => !attr.hidden);
              if (moduleAttrs.length === 0) return null;
              const moduleName = module.name;
              const isRepairFormOpen = activeRepairModule === moduleName;
              const moduleRepairHistory = moduleRepairs[moduleName] || [];

              return (
                <FormSection key={module.id} title={`${module.name} Attributes`} data-module-name={module.name}>
                  <div className="space-y-3">
                    {moduleAttrs.map((attr) => (
                      <div key={`${module.id}-${attr.name}`} data-error-key={`${module.id}_${attr.name}`} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                          {attr.name}
                          {attr.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {attr.type === 'boolean' ? (
                          <div className="flex gap-2">
                            {[
                              { value: 'true', label: 'Yes' },
                              { value: 'false', label: 'No' }
                            ].map(opt => (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => updateDeviceModuleAttributeValue(module.id, attr.name, opt.value)}
                                className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                  (deviceModuleAttributeValues[`${module.id}_${attr.name}`] || 'false') === opt.value
                                    ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        ) : attr.type === 'dropdown' ? (
                          attr.options && Array.isArray(attr.options) && attr.options.length <= 5 ? (
                            <div className="flex gap-2">
                              {attr.options.map((option, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => updateDeviceModuleAttributeValue(module.id, attr.name, option)}
                                  className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                    (deviceModuleAttributeValues[`${module.id}_${attr.name}`] || '') === option
                                      ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <select
                              value={deviceModuleAttributeValues[`${module.id}_${attr.name}`] || ''}
                              onChange={(e) => updateDeviceModuleAttributeValue(module.id, attr.name, e.target.value)}
                              className={`w-full h-7 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent text-xs ${errors[`${module.id}_${attr.name}`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                            >
                              <option value="">Select...</option>
                              {attr.options && Array.isArray(attr.options) && attr.options.map((option, idx) => (
                                <option key={idx} value={option}>{option}</option>
                              ))}
                            </select>
                          )
                        ) : attr.type === 'date' ? (
                          <input
                            type="date"
                            value={deviceModuleAttributeValues[`${module.id}_${attr.name}`] || ''}
                            onChange={(e) => updateDeviceModuleAttributeValue(module.id, attr.name, e.target.value)}
                            className={`w-full h-7 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent text-xs ${errors[`${module.id}_${attr.name}`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                          />
                        ) : attr.type === 'number' ? (
                          <input
                            type="number"
                            value={deviceModuleAttributeValues[`${module.id}_${attr.name}`] || ''}
                            onChange={(e) => updateDeviceModuleAttributeValue(module.id, attr.name, e.target.value)}
                            className={`w-full h-7 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent text-xs ${errors[`${module.id}_${attr.name}`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                          />
                        ) : (
                          <input
                            type="text"
                            value={deviceModuleAttributeValues[`${module.id}_${attr.name}`] || ''}
                            onChange={(e) => updateDeviceModuleAttributeValue(module.id, attr.name, e.target.value)}
                            className={`w-full h-7 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent text-xs ${errors[`${module.id}_${attr.name}`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                            placeholder={`Enter ${attr.name.toLowerCase()}`}
                          />
                        )}
                        {errors[`${module.id}_${attr.name}`] && (
                          <p className="mt-1 text-xs text-red-600">{errors[`${module.id}_${attr.name}`]}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Module Repair Section - Only show in edit mode */}
                  {!isCreateMode && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                          <Wrench className="h-4 w-4 mr-2 text-gray-600" />
                          Module Repairs
                        </h3>
                        {!isRepairFormOpen && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleOpenRepairForm(moduleName)}
                          >
                            Repair
                          </Button>
                        )}
                      </div>

                      {/* Previous Repairs */}
                      {moduleRepairHistory.length > 0 && (
                        <div className="mb-4 space-y-2">
                          {moduleRepairHistory.map((repair) => {
                            const details = typeof repair.details === 'string' ? JSON.parse(repair.details) : repair.details;
                            const timestamp = formatDateTime(repair.timestamp);
                            const repairTypeLabel = {
                              'paid': 'Paid',
                              'free_of_charge': 'Free of charge',
                              'warranty': 'Warranty'
                            }[details.repair_type] || details.repair_type;
                            
                            // Truncate description if too long
                            const maxLength = 80;
                            const description = details.repair_description.length > maxLength
                              ? details.repair_description.substring(0, maxLength) + '...'
                              : details.repair_description;

                            return (
                              <div key={repair.id} className="text-sm p-3 bg-gray-50 rounded-md">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <span className="text-gray-600">{timestamp}</span>
                                    <span className="mx-2 text-gray-400">•</span>
                                    <span className="font-medium text-gray-900">{repair.performed_by_name}</span>
                                    <span className="mx-2 text-gray-400">•</span>
                                    <span className="text-naviter-blue font-medium">{repairTypeLabel}</span>
                                    <div className="mt-1 text-gray-700">{description}</div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Repair Form */}
                      {isRepairFormOpen && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          {repairError && (
                            <AlertMessage type="error" message={repairError} className="mb-3" />
                          )}
                          
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                Repair Description
                              </label>
                              <Textarea
                                value={repairFormData.repair_description}
                                onChange={(e) => handleRepairFormChange('repair_description', e.target.value)}
                                placeholder="Describe the repair performed..."
                                rows={2}
                                size="sm"
                                className="w-full"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                Repair Type <span className="text-red-500">*</span>
                              </label>
                              <div className="flex gap-2">
                                {[
                                  { value: 'paid', label: 'Paid' },
                                  { value: 'free_of_charge', label: 'Free of charge' },
                                  { value: 'warranty', label: 'Warranty' }
                                ].map(opt => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => handleRepairFormChange('repair_type', opt.value)}
                                    className={`flex-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                      repairFormData.repair_type === opt.value
                                        ? 'bg-blue-50 text-naviter-blue border-naviter-blue font-semibold'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="flex gap-2 pt-2">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleSaveRepair(moduleName)}
                                disabled={repairSaving}
                              >
                                {repairSaving ? 'Saving...' : 'Save'}
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleCloseRepairForm}
                                disabled={repairSaving}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </FormSection>
              );
            })}

          {/* Legacy Data Section - Only show in edit mode if there are hidden attributes with values */}
          {!isCreateMode && (() => {
              const hiddenProductAttrs = productConfig.productAttributes.filter(attr => attr.hidden && deviceAttributeValues[attr.name]);
              const hiddenModules = productConfig.assignedModules.filter(module => {
                if (!module.hidden) return false;
                const moduleAttrs = productConfig.moduleAttributesMap[module.id] || [];
                return moduleAttrs.some(attr => deviceModuleAttributeValues[`${module.id}_${attr.name}`]);
              });
              
              return (hiddenProductAttrs.length > 0 || hiddenModules.length > 0) && (
                <FormSection 
                  title={
                    <div className="flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2 text-gray-500" />
                      <span>Legacy Data</span>
                    </div>
                  } 
                  className="!bg-gray-50 border-l-4 border-gray-400"
                >
                  <p className="text-sm text-gray-600 mb-4">
                    These fields are no longer used for new devices but are preserved for this device.
                  </p>

                  {/* Legacy Product Attributes */}
                  {hiddenProductAttrs.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-md font-medium text-gray-600 mb-3">Legacy Product Attributes</h3>
                      <div className="space-y-3">
                        {hiddenProductAttrs.map((attr) => (
                          <div key={attr.name} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                            <label className="block text-sm font-medium text-gray-500 mb-1">
                              {attr.name}
                            </label>
                            <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-sm text-gray-700">
                              {deviceAttributeValues[attr.name] || <span className="text-gray-400 italic">No value</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Legacy Module Attributes */}
                  {hiddenModules.map((module) => {
                    const moduleAttrs = (productConfig.moduleAttributesMap[module.id] || []).filter(attr => 
                      deviceModuleAttributeValues[`${module.id}_${attr.name}`]
                    );
                    if (moduleAttrs.length === 0) return null;

                    return (
                      <div key={module.id} className="mb-6 last:mb-0">
                        <h3 className="text-md font-medium text-gray-600 mb-3">Legacy {module.name} Attributes</h3>
                        <div className="space-y-3">
                          {moduleAttrs.map((attr) => (
                            <div key={`${module.id}-${attr.name}`} className="border-b border-gray-200 pb-3 last:border-b-0 last:pb-0">
                              <label className="block text-sm font-medium text-gray-500 mb-1">
                                {attr.name}
                              </label>
                              <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-sm text-gray-700">
                                {deviceModuleAttributeValues[`${module.id}_${attr.name}`] || <span className="text-gray-400 italic">No value</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </FormSection>
              );
            })()}

          {/* Show message when no attributes are available */}
          {((isCreateMode && !(deviceData.product_name || deviceData.model)) || (!isCreateMode && productConfig.productAttributes.length === 0 && productConfig.assignedModules.length === 0)) && (
            <div className="bg-white shadow rounded-lg p-6">
                <div className="text-center py-8 text-gray-500">
                  <p>
                    {(isCreateMode && !(deviceData.product_name || deviceData.model))
                      ? 'Product attributes will appear here after selecting a product type.'
                      : 'No attributes configured for this product type.'
                    }
                  </p>
                </div>
            </div>
          )}

          {/* Repairs History Section - Only show in edit mode */}
          {!isCreateMode && (
            <FormSection title="Repairs History">
              <div className="flex justify-end mb-3">
                <Button
                  variant="primary"
                  onClick={handleCreateRepair}
                  icon={Plus}
                  size="sm"
                >
                  New Repair
                </Button>
              </div>

                {repairsError && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                      <p className="text-red-800">{repairsError}</p>
                    </div>
                  </div>
                )}

                {repairsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">Loading repairs history...</div>
                  </div>
                ) : repairs.length === 0 ? (
                  <EmptyState message="No repairs found for this device" />
                ) : (
                  <DataTable>
                    <TableHeader>
                      <tr>
                        <TableHeaderCell>Issue Description</TableHeaderCell>
                        <TableHeaderCell>Status</TableHeaderCell>
                        <TableHeaderCell>Reported</TableHeaderCell>
                        <TableHeaderCell>Technician</TableHeaderCell>
                      </tr>
                    </TableHeader>
                    <TableBody>
                      {repairs.map((repair) => (
                        <TableRow 
                          key={repair.id}
                          clickable={true}
                          href={`/repairs/${repair.id}/view`}
                          onClick={() => handleViewRepair(repair)}
                        >
                          <TableCell className="max-w-xs">
                            <div className="text-sm text-gray-900 truncate">
                              {repair.acceptance_notes || 'No description provided'}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {repair.id}
                            </div>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={repair.status} variant="repair" />
                          </TableCell>
                          <TableCell className="text-gray-500">
                            {repair.reported_date ? formatDate(repair.reported_date) : 'N/A'}
                          </TableCell>
                          <TableCell className="text-gray-500">
                            {repair.assigned_to_name || 'Unassigned'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </DataTable>
                )}
            </FormSection>
          )}

          {/* File Attachments Section */}
          <FormSection title="Attachments">
            <FileUploadManager
              entityType="devices"
              entityId={deviceData.id}
              disabled={saving}
              compact
            />
          </FormSection>
        </div>

        {/* Duplicate Action Buttons - Bottom */}
        <div className="mt-4">
          <RepairActionButtons {...getActionButtons()} />
        </div>
        </div>
    </div>
  );
};

export default DeviceEdit;